<template>
  <b-modal
    ref="modal"
    title="Manage Stores / Branches"
    size="lg"
    hide-footer
    @show="loadBranches"
  >
    <div class="d-flex justify-content-between align-items-center mb-3">
      <p class="text-muted mb-0" style="font-size:.85rem;">
        You have <strong>{{ branches.length }}</strong> store{{ branches.length !== 1 ? 's' : '' }}.
        <span v-if="branches.length >= 10" class="text-danger ml-1">Branch limit reached (10).</span>
      </p>
      <button
        class="btn btn-sm btn-primary"
        :disabled="branches.length >= 10"
        @click="openCreateModal"
      >
        <i class="bi bi-plus mr-1"></i>Add Store
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner /><p class="mt-2 text-muted">Loading branches…</p>
    </div>

    <!-- Branch list -->
    <div v-else>
      <div
        v-for="branch in branches"
        :key="branch.id"
        class="bm-row"
        :class="{ 'bm-row--current': isCurrent(branch) }"
      >
        <!-- Name + inline rename -->
        <div class="bm-row__main">
          <div v-if="renaming === branch.company_id" class="d-flex align-items-center">
            <input
              :ref="'rename-' + branch.company_id"
              v-model="renameValue"
              class="form-control form-control-sm bm-rename-input"
              @keyup.enter="submitRename(branch)"
              @keyup.esc="cancelRename"
            />
            <button class="btn btn-sm btn-success ml-2" :disabled="renameSaving" @click="submitRename(branch)">
              <b-spinner v-if="renameSaving" small /><i v-else class="bi bi-check"></i>
            </button>
            <button class="btn btn-sm btn-outline-secondary ml-1" @click="cancelRename">
              <i class="bi bi-x"></i>
            </button>
          </div>
          <div v-else class="d-flex align-items-center">
            <span class="bm-row__name">{{ branch.company?.name || 'Unknown store' }}</span>
            <span v-if="isCurrent(branch)" class="badge badge-primary ml-2" style="font-size:.7rem;">Current</span>
          </div>
          <small class="text-muted bm-row__meta">
            ID: {{ branch.company_id.substring(0, 8) }}…
            <span v-if="branch.company?.billing" class="ml-2">
              · Due: {{ formatDate(branch.company.billing.due_date) }}
            </span>
          </small>
        </div>

        <!-- Actions -->
        <div class="bm-row__actions">
          <!-- Switch -->
          <button
            v-if="!isCurrent(branch)"
            class="btn btn-sm btn-outline-secondary"
            title="Switch to this branch"
            @click="switchTo(branch)"
          >
            <i class="bi bi-arrow-right-circle mr-1"></i>Switch
          </button>

          <!-- Rename -->
          <button
            v-if="renaming !== branch.company_id"
            class="btn btn-sm btn-outline-secondary"
            title="Rename"
            @click="startRename(branch)"
          >
            <i class="bi bi-pencil"></i> Edit
          </button>

          <!-- Delete -->
          <button
            class="btn btn-sm btn-outline-danger"
            title="Delete branch"
            :disabled="isCurrent(branch)"
            @click="confirmDelete(branch)"
          >
            <i class="bi bi-trash"></i> Delete
          </button>
        </div>
      </div>

      <p v-if="!branches.length && !loading" class="text-center text-muted py-4">
        No branches found.
      </p>
    </div>

    <!-- ── CREATE STORE MODAL (existing flow preserved) ── -->
    <b-modal ref="createModal" title="Add a New Store" hide-footer @hidden="resetCreateForm">
      <form @submit.prevent="submitCreate(false)">
        <div class="form-group">
          <label>Store name *</label>
          <input class="form-control" type="text" placeholder="Store name" v-model="newStore.name" required />
        </div>
        <div class="form-group">
          <div class="custom-control custom-switch">
            <input type="checkbox" class="custom-control-input" id="bm-copyInventory" v-model="newStore.copyInventory" />
            <label class="custom-control-label" for="bm-copyInventory">Copy Inventory</label>
          </div>
        </div>
        <div v-if="newStore.copyInventory" class="form-group">
          <div class="custom-control custom-switch">
            <input type="checkbox" class="custom-control-input" id="bm-qtyToZero" v-model="newStore.qtyToZero" />
            <label class="custom-control-label" for="bm-qtyToZero">Copy Quantities as well</label>
          </div>
        </div>
        <div class="form-group">
          <div class="custom-control custom-switch">
            <input type="checkbox" class="custom-control-input" id="bm-switchToNew" v-model="newStore.switchToNew" />
            <label class="custom-control-label" for="bm-switchToNew">Switch to the new store after creating</label>
          </div>
        </div>

        <!-- Plan upgrade notice (shown when backend returns requires_confirmation) -->
        <div v-if="createPlanWarning" class="alert alert-warning d-flex align-items-start mb-3">
          <i class="bi bi-exclamation-triangle-fill mr-2 mt-1" style="font-size:1.1rem;"></i>
          <div>
            <strong>Plan upgrade may be required.</strong>
            <p class="mb-0 mt-1" style="font-size:.85rem;">{{ createPlanWarning }}</p>
          </div>
        </div>

        <div class="d-flex">
          <button v-if="!createPlanWarning" class="btn btn-primary" :disabled="creating" type="submit">
            <b-spinner v-if="creating" small class="mr-1" />
            Create Store
          </button>
          <template v-else>
            <button class="btn btn-outline-secondary mr-2" type="button" @click="createPlanWarning = ''">
              Cancel
            </button>
            <button class="btn btn-warning" :disabled="creating" type="button" @click="submitCreate(true)">
              <b-spinner v-if="creating" small class="mr-1" />
              Create Anyway
            </button>
          </template>
        </div>
      </form>
    </b-modal>

    <!-- ── DELETE SAFETY MODAL ── -->
    <b-modal ref="safetyModal" title="Delete Branch" size="md" hide-footer no-close-on-backdrop @hidden="cancelDelete">

      <!-- Loading deps -->
      <div v-if="deleteStep === 'loading'" class="text-center py-5">
        <b-spinner /><p class="mt-2 text-muted">Checking branch data…</p>
      </div>

      <!-- Review & confirm -->
      <div v-else-if="deleteStep === 'review'">
        <p class="mb-3">
          You are about to permanently delete <strong>{{ deleteTarget?.company?.name || 'this store' }}</strong>.
        </p>

        <!-- Dependency breakdown -->
        <div v-if="hasDependencies" class="card mb-3" style="border-color:#dc3545;">
          <div class="card-header py-2 text-white" style="background:#dc3545;font-size:.85rem;">
            <i class="bi bi-exclamation-triangle-fill mr-1"></i>
            <strong>This branch has existing data that will be deleted</strong>
          </div>
          <div class="card-body py-2 px-3">
            <table class="table table-sm mb-2">
              <tbody>
                <tr v-if="deleteDeps.inventory > 0">
                  <td class="pl-0"><i class="bi bi-box mr-1 text-muted"></i>Inventory Items</td>
                  <td class="text-right font-weight-bold">{{ deleteDeps.inventory.toLocaleString() }}</td>
                </tr>
                <tr v-if="deleteDeps.orders > 0">
                  <td class="pl-0"><i class="bi bi-receipt mr-1 text-muted"></i>Sales / Orders</td>
                  <td class="text-right font-weight-bold">{{ deleteDeps.orders.toLocaleString() }}</td>
                </tr>
                <tr v-if="deleteDeps.employees > 0">
                  <td class="pl-0"><i class="bi bi-people mr-1 text-muted"></i>Employees</td>
                  <td class="text-right font-weight-bold">{{ deleteDeps.employees.toLocaleString() }}</td>
                </tr>
              </tbody>
            </table>
            <div class="custom-control custom-checkbox">
              <input type="checkbox" class="custom-control-input" id="bm-ackDeps" v-model="ackDeps" />
              <label class="custom-control-label text-danger" for="bm-ackDeps" style="font-size:.85rem;">
                I understand all data listed above will be permanently deleted and cannot be recovered.
              </label>
            </div>
          </div>
        </div>

        <!-- Billing cycle warning -->
        <div v-if="deleteDeps && deleteDeps.billing_cycle_active" class="card mb-3" style="border-color:#856404;">
          <div class="card-header py-2" style="background:#fff3cd;font-size:.85rem;">
            <i class="bi bi-calendar-event mr-1"></i>
            <strong>Billing Cycle Activity Detected</strong>
          </div>
          <div class="card-body py-2 px-3">
            <p class="mb-2" style="font-size:.85rem;">
              This branch recorded <strong>{{ deleteDeps.billing_cycle_detail.orders }} sale{{ deleteDeps.billing_cycle_detail.orders !== 1 ? 's' : '' }}</strong>
              in the current billing period (<strong>{{ deleteDeps.current_period }}</strong>).
              Deleting it now does not automatically exempt it from billing for this cycle.
            </p>
            <div class="custom-control custom-checkbox">
              <input type="checkbox" class="custom-control-input" id="bm-ackBilling" v-model="ackBilling" />
              <label class="custom-control-label" for="bm-ackBilling" style="font-size:.85rem;">
                I understand this branch may still be billed for the current period.
              </label>
            </div>
          </div>
        </div>

        <!-- Clean branch (no deps, no billing activity) -->
        <p v-if="!hasDependencies && !(deleteDeps && deleteDeps.billing_cycle_active)" class="text-muted mb-3" style="font-size:.85rem;">
          No active data found on this branch. This action cannot be undone.
        </p>

        <div class="d-flex justify-content-end">
          <button class="btn btn-outline-secondary mr-2" @click="$refs.safetyModal.hide()">Cancel</button>
          <button class="btn btn-danger" :disabled="!canConfirmDelete || deleting" @click="executeDelete">
            <b-spinner v-if="deleting" small class="mr-1" />
            Delete Branch
          </button>
        </div>
      </div>

    </b-modal>

  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex'
import branchSwitch from '../../services/branchSwitch'

export default {
  name: 'BranchManagerModal',

  data() {
    return {
      loading:  false,
      branches: [],

      // Rename
      renaming:     null,
      renameValue:  '',
      renameSaving: false,

      // Create
      creating: false,
      createPlanWarning: '',
      newStore: { name: '', copyInventory: false, qtyToZero: false, switchToNew: true },

      // Delete safety
      deleteTarget:  null,
      deleteStep:    null,   // 'loading' | 'review'
      deleteDeps:    null,
      ackDeps:       false,
      ackBilling:    false,
      deleting:      false,
    }
  },

  computed: {
    ...mapGetters(['userDetails']),

    hasDependencies() {
      if (!this.deleteDeps) return false;
      return this.deleteDeps.inventory > 0 || this.deleteDeps.orders > 0 || this.deleteDeps.employees > 0;
    },

    canConfirmDelete() {
      if (!this.deleteDeps) return false;
      if (this.hasDependencies && !this.ackDeps) return false;
      if (this.deleteDeps.billing_cycle_active && !this.ackBilling) return false;
      return true;
    },
  },

  methods: {
    show() {
      this.$refs.modal.show()
    },

    isCurrent(branch) {
      return branch.company_id === this.userDetails?.company?.id
    },

    // ── LOAD ──────────────────────────────────────────────────────────────

    async loadBranches() {
      this.loading = true
      try {
        const { data } = await axios.get('/api/other-branches')
        this.branches = data
      } catch {
        this.$bvToast.toast('Failed to load branches.', { title: 'Error', variant: 'danger' })
      } finally {
        this.loading = false
      }
    },

    // ── SWITCH ────────────────────────────────────────────────────────────

    async switchTo(branch) {
      try {
        await branchSwitch.switchTo(this.$store, branch.company_id)
      } catch {
        this.$bvToast.toast('Failed to switch branch.', { title: 'Error', variant: 'danger' })
      }
    },

    // ── RENAME ────────────────────────────────────────────────────────────

    startRename(branch) {
      this.renaming    = branch.company_id
      this.renameValue = branch.company?.name || ''
      this.$nextTick(() => {
        const el = this.$refs['rename-' + branch.company_id]
        if (el) (Array.isArray(el) ? el[0] : el).focus()
      })
    },

    cancelRename() {
      this.renaming    = null
      this.renameValue = ''
    },

    async submitRename(branch) {
      if (!this.renameValue.trim()) return
      this.renameSaving = true
      try {
        await axios.put(`/api/branches/${branch.company_id}`, { name: this.renameValue.trim() })
        branch.company.name = this.renameValue.trim()
        this.cancelRename()
        this.$bvToast.toast('Branch renamed.', { title: 'Success', variant: 'success' })
      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message || 'Failed to rename branch.',
          { title: 'Error', variant: 'danger' }
        )
      } finally {
        this.renameSaving = false
      }
    },

    // ── CREATE ────────────────────────────────────────────────────────────

    openCreateModal() {
      this.$refs.createModal.show()
    },

    resetCreateForm() {
      this.newStore = { name: '', copyInventory: false, qtyToZero: false, switchToNew: true }
      this.createPlanWarning = ''
    },

    async submitCreate(confirmed = false) {
      if (!this.newStore.name.trim()) return
      this.creating = true
      try {
        const copyInventoryFrom = this.userDetails?.company?.id
        const { data } = await axios.post('/api/create-store', {
          ...this.newStore,
          copyInventoryFrom,
          confirmed,
        })

        // Backend needs explicit confirmation before proceeding
        if (data.requires_confirmation) {
          this.createPlanWarning = data.message
          return
        }

        this.$refs.createModal.hide()
        this.$bvToast.toast('Store created.', { title: 'Success', variant: 'success' })
        if (this.newStore.switchToNew) {
          setTimeout(() => window.location.reload(), 800)
        } else {
          await this.loadBranches()
        }
      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message || 'Failed to create store.',
          { title: 'Error', variant: 'danger' }
        )
      } finally {
        this.creating = false
      }
    },

    // ── DELETE ────────────────────────────────────────────────────────────

    async confirmDelete(branch) {
      this.deleteTarget = branch
      this.deleteDeps   = null
      this.ackDeps      = false
      this.ackBilling   = false
      this.deleteStep   = 'loading'
      this.$refs.safetyModal.show()

      try {
        const { data } = await axios.get(`/api/branches/${branch.company_id}/dependencies`)
        this.deleteDeps = data
        this.deleteStep = 'review'
      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message || 'Could not check branch data.',
          { title: 'Error', variant: 'danger' }
        )
        this.$refs.safetyModal.hide()
      }
    },

    cancelDelete() {
      this.deleteTarget = null
      this.deleteDeps   = null
      this.deleteStep   = null
      this.ackDeps      = false
      this.ackBilling   = false
    },

    async executeDelete() {
      if (!this.deleteTarget || !this.canConfirmDelete) return
      this.deleting = true
      try {
        await axios.delete(`/api/branches/${this.deleteTarget.company_id}`)
        this.branches = this.branches.filter(b => b.company_id !== this.deleteTarget.company_id)
        this.$refs.safetyModal.hide()
        this.$bvToast.toast('Branch deleted.', { title: 'Success', variant: 'success' })
      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message || 'Failed to delete branch.',
          { title: 'Error', variant: 'danger' }
        )
      } finally {
        this.deleting = false
      }
    },

    // ── HELPERS ───────────────────────────────────────────────────────────

    formatDate(date) {
      return date ? moment(date).format('DD MMM YYYY') : '—'
    },
  },
}
</script>

<style scoped>
.bm-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #f0f2f4;
  gap: 12px;
  transition: background .1s;
}
.bm-row:last-child { border-bottom: none; }
.bm-row:hover { background: #fafbfc; }
.bm-row--current { background: #f0f7ff; }

.bm-row__main { flex: 1; min-width: 0; }
.bm-row__name { font-size: 14px; font-weight: 600; color: #212529; }
.bm-row__meta { display: block; margin-top: 2px; }
.bm-row__actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }

.bm-rename-input { min-width: 160px; }
</style>
